import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Parque, ParqueTarifa } from "../types/common.types";
import formatCLP from "./formatCLP";

// Tarifas de entrada de todos los parques activos, para mandar a vendedores y aliados cada vez que
// cambia una tarifa. Reemplaza la hoja "Entradas parques" que se armaba a mano: sale de lo mismo que
// cobra el bot (pestaña Parques), así que no puede quedar desactualizada respecto del sistema.

// Orden de lectura: primero las tarifas por edad (de mayor a menor), después las especiales.
const ORDEN_CATEGORIAS = ['general', 'adulto mayor', 'joven', 'niño', 'infante', 'estudiante', 'necesidades especiales', 'indígena', 'atacameño'];
const ORDEN_NACIONALIDAD = [null, 'chileno', 'extranjero'];

const NARANJA: [number, number, number] = [240, 170, 130];
const CREMA: [number, number, number] = [250, 228, 210];

function indice(lista: any[], valor: any) {
  const i = lista.indexOf(valor);
  return i === -1 ? lista.length : i;
}

function ordenarTarifas(tarifas: ParqueTarifa[]) {
  return [...tarifas].sort((a, b) =>
    indice(ORDEN_CATEGORIAS, a.categoria) - indice(ORDEN_CATEGORIAS, b.categoria) ||
    indice(ORDEN_NACIONALIDAD, a.nacionalidad || null) - indice(ORDEN_NACIONALIDAD, b.nacionalidad || null) ||
    Number(b.precio_clp) - Number(a.precio_clp));
}

const vacio = (v: any) => v === null || v === undefined || v === '';

function edadTexto(t: ParqueTarifa) {
  if (vacio(t.edad_min) && vacio(t.edad_max)) return '';
  if (vacio(t.edad_max)) return `${t.edad_min} años o más`;
  return `${vacio(t.edad_min) ? 0 : t.edad_min} a ${t.edad_max} años`;
}

function categoriaTexto(t: ParqueTarifa) {
  const nombre = t.categoria.charAt(0).toUpperCase() + t.categoria.slice(1);
  return t.requiere_acreditacion ? `${nombre} *` : nombre;
}

function nacionalidadTexto(t: ParqueTarifa) {
  if (t.nacionalidad === 'chileno') return 'Chileno';
  if (t.nacionalidad === 'extranjero') return 'Extranjero';
  return 'Todos';
}

const precioTexto = (t: ParqueTarifa) => Number(t.precio_clp) === 0 ? 'Gratis' : formatCLP(Number(t.precio_clp));

export function filasDelParque(p: Parque): string[][] {
  return ordenarTarifas(p.tarifas).map(t => [categoriaTexto(t), nacionalidadTexto(t), edadTexto(t), precioTexto(t)]);
}

export default function buildParquesPdf(parques: Parque[], { fecha, logo }: { fecha: Date; logo?: string }) {
  const doc = new jsPDF();
  const margin = 14;
  const anchoPagina = doc.internal.pageSize.getWidth();
  const altoPagina = doc.internal.pageSize.getHeight();

  // Encabezado
  doc.setFillColor(...NARANJA);
  doc.rect(0, 0, anchoPagina, 30, 'F');
  if (logo) {
    try { doc.addImage(logo, 'PNG', margin, 4, 22, 22); } catch { /* sin logo */ }
  }
  doc.setTextColor(40, 30, 20);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text('ENTRADAS A PARQUES', anchoPagina / 2, 15, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(`Tarifas vigentes al ${fecha.toLocaleDateString('es-CL')} · valores por persona en pesos chilenos`, anchoPagina / 2, 22, { align: 'center' });

  const activos = parques.filter(p => p.activo && p.tarifas.length > 0)
    .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));

  let y = 38;
  for (const p of activos) {
    const filas = filasDelParque(p);
    // Un parque nunca se parte entre dos páginas: si no cabe entero, empieza en la siguiente.
    const altoEstimado = 7 + 6 + filas.length * 6;
    if (y + altoEstimado > altoPagina - 18) {
      doc.addPage();
      y = 16;
    }
    autoTable(doc, {
      startY: y,
      head: [
        [{ content: p.nombre.toUpperCase(), colSpan: 4, styles: { halign: 'center', fillColor: NARANJA, textColor: [40, 30, 20], fontSize: 11 } }],
        ['Categoría', 'Nacionalidad', 'Edad', 'Precio'],
      ],
      body: filas,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 1.6, textColor: [40, 30, 20], lineColor: [120, 110, 100], lineWidth: 0.2 },
      headStyles: { fillColor: CREMA, textColor: [40, 30, 20], fontStyle: 'bold' },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 32 },
        2: { cellWidth: 38 },
        3: { cellWidth: 28, halign: 'right', fontStyle: 'bold' },
      },
      margin: { left: margin, right: margin },
    });
    y = (doc as any).lastAutoTable.finalY + 7;
  }

  // Nota al pie de la última página
  if (y > altoPagina - 22) { doc.addPage(); y = 16; }
  doc.setFontSize(8);
  doc.setTextColor(90, 80, 70);
  doc.text('* Requiere acreditación (credencial de estudiante, certificado de discapacidad o de pueblo originario, según corresponda).', margin, y);
  doc.text('Edades inclusivas. Las tarifas las fija cada parque y pueden cambiar sin previo aviso.', margin, y + 4.5);

  const paginas = doc.getNumberOfPages();
  for (let i = 1; i <= paginas; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(140, 130, 120);
    doc.text(`Molantur Atacama Tours · página ${i} de ${paginas}`, anchoPagina / 2, altoPagina - 8, { align: 'center' });
  }

  return doc;
}
